import { hasConnectedMcpStatus, normalizeMCPUrl } from '@littlebird/mcp';
import {
  MeetingCreationCancelledError,
  type Routine,
  useStores,
} from '@littlebird/sdk';
import { parseRoutineSchedule } from '@littlebird/ui-utils';
import { differenceInMinutes } from 'date-fns/differenceInMinutes';
import { format } from 'date-fns/format';
import { formatDistanceToNow } from 'date-fns/formatDistanceToNow';
import { isToday } from 'date-fns/isToday';
import { subDays } from 'date-fns/subDays';
import { observer } from 'mobx-react-lite';
import { AnimatePresence, motion } from 'motion/react';
import {
  useEffect,
  useMemo,
  useState,
  type ComponentType,
  type ReactNode,
} from 'react';
import { toast } from 'sonner';

import { Icons } from '@/assets/icons/icons';
import { ModalsManager } from '@/components/modals/modalsManager';
import { Button, Time, XStack, YStack } from '@/components/ui';
import { SegmentedControl } from '@/components/ui/segmented-control';
import {
  FEED_HOVER_REVEAL_CLASS,
  FEED_ROW_END_CTA_CLASS,
} from '@/features/action-cards-view/feed/feedStyles';
import { FeedTrayCta } from '@/features/action-cards-view/feed/FeedTrayCta';
import { contextChatStore } from '@/features/context-chat/context-chat.state';
import { electronApis } from '@/features/electron-api';
import { FEATURES, hasFeature } from '@/features/feature-flags';
import { AddIntegrationsModal } from '@/features/integrations/add-integrations-modal';
import type { IntegrationTarget } from '@/features/integrations/integrations.types';
import {
  useMCPCatalogQuery,
  useMCPServersQuery,
} from '@/features/mcp/mcp.query';
import { CalendarSyncControl } from '@/features/meetings/calendar-sync-control';
import { subscriptionStore } from '@/features/subscription';
import { useTabs } from '@/features/tabs';
import { useHashRoute } from '@/hooks/useHashRoute';
import { getIntegrationIcon } from '@/integrations/integration-icons';
import { connectIntegration } from '@/integrations/integrations.utils';
import { useIntegrationState } from '@/integrations/useIntegrationState';
import { cn } from '@/lib/utils';
import { trackEvent } from '@/utils/analytics';
import { AppCapability, isCapable } from '@/utils/capabilities';
import { formatTimeFromHHmm } from '@/utils/date';

import {
  HOME_SCHEDULE_MAX_ROWS,
  performScheduleJoin,
  selectScheduleState,
  type ScheduleMeetingRow,
  type ScheduleState,
} from './schedule';
import {
  useWidgetDisplay,
  type WidgetDisplayMode,
} from './widget-display.context';
import { Widget } from './widget.parts';
import {
  useChatsOn,
  useHasCalendarConnected,
  useMeetingsOn,
  useScheduleMeetings,
  useTodaysRoutines,
  type RoutineState,
} from './widgets.hooks';

import type { ScheduleFilterMode } from './widgets.types';

/** Cloud calendars that on-demand `POST /calendar/sync` can refresh. */
const SYNCABLE_CALENDAR_TOOLKITS = new Set(['googlecalendar', 'outlook']);

const useHasSyncableCalendarConnected = (): boolean => {
  const { integrationStates } = useIntegrationState();
  return integrationStates.some(
    (s) => s.isConnected && SYNCABLE_CALENDAR_TOOLKITS.has(s.info.toolkit_name)
  );
};

type ScheduleViewProps = {
  state: ScheduleState;
  filterMode?: ScheduleFilterMode;
  onFilterChange?: (mode: ScheduleFilterMode) => void;
  onOpenPrep: (eventId: string, meetingId: string | undefined) => void;
  onJoin: (eventId: string, joinUrl: string) => void;
  onViewAll: () => void;
  onConnectCalendar: () => void;
  extraHeaderAccessory?: ReactNode;
};

const ScheduleFilterTabs = ({
  filterMode,
  onFilterChange,
  mode,
}: {
  filterMode: ScheduleFilterMode;
  onFilterChange: (mode: ScheduleFilterMode) => void;
  mode: WidgetDisplayMode;
}) => {
  const triggerClassName = cn(
    'rounded-full px-2',
    mode === 'home' ? 'text-sm' : 'text-[length:calc(11rem/14)]'
  );
  return (
    <SegmentedControl.Root
      value={filterMode}
      onValueChange={(value) => onFilterChange(value as ScheduleFilterMode)}
    >
      <SegmentedControl.List className="h-6 rounded-full before:rounded-full">
        <SegmentedControl.Trigger value="all" className={triggerClassName}>
          All
        </SegmentedControl.Trigger>
        <SegmentedControl.Trigger value="meetings" className={triggerClassName}>
          Meetings
        </SegmentedControl.Trigger>
      </SegmentedControl.List>
    </SegmentedControl.Root>
  );
};

const ConnectCalendarEmpty = ({
  description,
  onConnect,
}: {
  description: string;
  onConnect: () => void;
}) => (
  <Widget.Empty
    icon={Icons.calendar}
    title="Connect your calendar"
    action={onConnect}
  >
    {description}
  </Widget.Empty>
);

const HOME_EMPTY_SCHEDULE_COUNT: Partial<
  Record<ScheduleState['kind'], string>
> = {
  'empty-no-calendar': 'No calendar connected',
  'empty-clear-today': 'No meetings today',
};

const scheduleCount = (state: ScheduleState, mode: WidgetDisplayMode) => {
  if (state.kind === 'populated') return upcomingCountLabel(state.totalCount);
  return mode === 'home' ? HOME_EMPTY_SCHEDULE_COUNT[state.kind] : undefined;
};

const scheduleHeaderAccessory = (
  state: ScheduleState,
  mode: WidgetDisplayMode,
  {
    filterMode,
    onFilterChange,
    onConnectCalendar,
  }: Pick<
    ScheduleViewProps,
    'filterMode' | 'onFilterChange' | 'onConnectCalendar'
  >
) => {
  if (state.kind === 'loading') return undefined;
  if (state.kind === 'empty-no-calendar')
    return mode === 'home' ? (
      <Widget.HeaderButton onClick={onConnectCalendar}>
        Connect
      </Widget.HeaderButton>
    ) : undefined;
  if (!filterMode || !onFilterChange) return undefined;
  return (
    <ScheduleFilterTabs
      filterMode={filterMode}
      onFilterChange={onFilterChange}
      mode={mode}
    />
  );
};

const ScheduleEmptyBody = ({
  state,
  onConnectCalendar,
}: {
  state: ScheduleState;
  onConnectCalendar: () => void;
}) => {
  switch (state.kind) {
    case 'empty-no-calendar':
      return (
        <ConnectCalendarEmpty
          description="See upcoming meetings and prep notes here."
          onConnect={onConnectCalendar}
        />
      );
    case 'empty-clear-week':
      return (
        <Widget.Empty icon={Icons.calendar} title="Calendar's clear this week">
          Nothing scheduled in the next 7 days.
        </Widget.Empty>
      );
    case 'empty-clear-today':
      return (
        <Widget.Empty icon={Icons.calendar} title="No meetings today">
          Events with no other guests are hidden. Turn off Ignore solo events in
          Meetings settings to show them.
        </Widget.Empty>
      );
    case 'loading':
    case 'populated':
      return null;
  }
};

export const ScheduleView = ({
  state,
  filterMode,
  onFilterChange,
  onOpenPrep,
  onJoin,
  onViewAll,
  onConnectCalendar,
  extraHeaderAccessory,
}: ScheduleViewProps) => {
  const { mode } = useWidgetDisplay();

  return (
    <Widget id="upcoming-meetings">
      <Widget.Header
        title="Schedule"
        titleClusterClassName="group/calendar-sync"
        titleAccessory={extraHeaderAccessory}
        count={scheduleCount(state, mode)}
        accessory={scheduleHeaderAccessory(state, mode, {
          filterMode,
          onFilterChange,
          onConnectCalendar,
        })}
      />
      <Widget.Body>
        {state.kind === 'loading' && <Widget.Skeleton rows={2} />}

        {mode === 'card' && (
          <ScheduleEmptyBody
            state={state}
            onConnectCalendar={onConnectCalendar}
          />
        )}

        {state.kind === 'populated' && (
          <YStack className="gap-1">
            {state.rows.map((row) => (
              <ScheduleMeetingRowView
                key={row.key}
                row={row}
                onOpenPrep={() => onOpenPrep(row.eventId, row.meetingId)}
                onJoin={
                  row.joinUrl
                    ? () => onJoin(row.eventId, row.joinUrl!)
                    : undefined
                }
              />
            ))}
            {state.hasMore && (
              <Widget.Row align="center" onClick={onViewAll}>
                <span className="flex-1 text-xs text-foreground-muted">
                  View all {state.totalCount} meetings
                </span>
                <Icons.chevronRight className="size-3.5 text-foreground-subtle shrink-0" />
              </Widget.Row>
            )}
          </YStack>
        )}
      </Widget.Body>
    </Widget>
  );
};

type ScheduleMeetingRowViewProps = {
  row: ScheduleMeetingRow;
  onOpenPrep: () => void;
  onJoin?: () => void;
};

const ScheduleMeetingRowView = ({
  row,
  onOpenPrep,
  onJoin,
}: ScheduleMeetingRowViewProps) => {
  const now = new Date();
  const minutesUntil = differenceInMinutes(row.startDate, now);
  const isHappeningNow = minutesUntil <= 0;
  const canJoin = Boolean(onJoin) && row.endDateMs >= now.getTime();

  const indicatorClass = isHappeningNow
    ? 'bg-brand-emerald-green'
    : 'bg-brand-jordy-blue';

  const subtitle = !row.isMeeting
    ? formatEventDuration(row.startDate, row.endDateMs)
    : row.hasPrep
      ? 'Prep ready'
      : 'No prep yet · click to draft';

  return (
    <Widget.Row
      onClick={row.isMeeting ? onOpenPrep : undefined}
      className="group/feed-row"
    >
      <span
        className={cn(
          'w-[3px] self-stretch rounded-full shrink-0 mt-0.5',
          indicatorClass
        )}
        aria-hidden
      />
      <YStack className="w-[calc(60rem/14)] shrink-0 items-start gap-0.5">
        <Time
          date={row.startDate}
          className={cn(
            'text-sm font-medium whitespace-nowrap',
            isHappeningNow ? 'text-brand-emerald-green' : 'text-foreground'
          )}
        />
        <span className="text-[length:calc(11rem/14)] text-foreground-subtle font-medium uppercase tracking-wider">
          {isHappeningNow
            ? 'now'
            : isToday(row.startDate)
              ? relativeMinutesLabel(minutesUntil)
              : format(row.startDate, 'EEE').toLowerCase()}
        </span>
      </YStack>
      <YStack className="flex-1 min-w-0 gap-0.5">
        <span className="text-sm text-foreground truncate font-medium no-record">
          {row.summary}
        </span>
        <span className="text-[length:calc(11rem/14)] text-foreground-muted truncate">
          {subtitle}
        </span>
      </YStack>
      {canJoin && onJoin && (
        <span
          className={cn(
            'ac-scale shrink-0 self-center',
            FEED_HOVER_REVEAL_CLASS,
            FEED_ROW_END_CTA_CLASS
          )}
        >
          <FeedTrayCta
            icon={Icons.video}
            label="Join"
            accessibleLabel={`Join ${row.summary}`}
            onClick={(e) => {
              e.stopPropagation();
              onJoin();
            }}
          />
        </span>
      )}
    </Widget.Row>
  );
};

export const UpcomingMeetingsWidget = observer(() => {
  const { configStore: widgetConfig, mode } = useWidgetDisplay();
  const todayOnly = mode === 'home';
  const { meetings, totalCount, hasMore, isLoading } = useScheduleMeetings({
    excludeSoloEvents: false,
    filterMode: widgetConfig.scheduleFilterMode,
    todayOnly,
    maxRows: mode === 'home' ? HOME_SCHEDULE_MAX_ROWS : undefined,
  });
  const hasCalendar = useHasCalendarConnected();
  const state = selectScheduleState({
    meetings,
    totalCount,
    hasMore,
    todayOnly,
    hasCalendar,
    isLoading,
  });
  const { openTab } = useTabs();
  const { setHash } = useHashRoute();
  const hasSyncableCalendar = useHasSyncableCalendarConnected();
  const meetingsStore = useStores().meetings;
  const canRecord = isCapable(AppCapability.RecordMeeting);

  const rows = state.kind === 'populated' ? state.rows : [];
  const rowsById = new Map(rows.map((row) => [row.eventId, row]));

  const scheduleContext = (eventId: string) => {
    const row = rowsById.get(eventId);
    if (!row) return {};
    return {
      minutes_until: differenceInMinutes(row.startDate, new Date()),
      is_next_meeting: eventId === rows[0]?.eventId,
      has_prep: row.hasPrep,
    };
  };

  return (
    <ScheduleView
      state={state}
      filterMode={widgetConfig.scheduleFilterMode}
      onFilterChange={(mode) => widgetConfig.setScheduleFilterMode(mode)}
      onOpenPrep={async (eventId, meetingId) => {
        trackEvent('WIDGETS_SCHEDULE_PREP_CLICKED', {
          has_meeting_id: Boolean(meetingId),
          ...scheduleContext(eventId),
        });
        const row = rowsById.get(eventId);
        if (!row) return;
        let id = meetingId;
        if (!id) {
          const meeting = await meetingsStore.ensurePrepMeeting(
            row.eventId,
            row.summary,
            {
              onError: (error) => {
                if (error instanceof MeetingCreationCancelledError) return;
                toast.error('Failed to create meeting note.');
              },
            }
          );
          id = meeting.id;
        }
        openTab(
          { path: `/meetings/${id}`, search: { tab: 'notes' } },
          { reuseExisting: true }
        );
      }}
      onJoin={(eventId, joinUrl) => {
        trackEvent('WIDGETS_SCHEDULE_JOIN_CLICKED', scheduleContext(eventId));
        performScheduleJoin(
          {
            joinUrl,
            eventId,
            title: rowsById.get(eventId)?.summary,
            canRecord,
          },
          {
            openExternal: (url) =>
              electronApis.system.openExternal(url, { preferMeetingApp: true }),
            markUpcomingMeetingJoined: (id) =>
              void electronApis.meetings.markUpcomingMeetingJoined(id),
            startMeetingNote: (input) =>
              electronApis.meetings.requestStartMeetingNote(input),
          }
        );
      }}
      onViewAll={() => {
        trackEvent('WIDGETS_SCHEDULE_VIEW_ALL_CLICKED', {
          total_count: state.kind === 'populated' ? state.totalCount : 0,
        });
        openTab({ path: '/meetings' }, { reuseExisting: true });
      }}
      onConnectCalendar={() => {
        trackEvent('WIDGETS_SCHEDULE_CONNECT_CALENDAR_CLICKED', {
          widget: 'schedule',
        });
        setHash('settings/Integrations');
      }}
      extraHeaderAccessory={
        hasSyncableCalendar ? (
          <CalendarSyncControl
            onReconnect={() => setHash('settings/Integrations')}
          />
        ) : undefined
      }
    />
  );
});

const upcomingCountLabel = (n: number): string | undefined => {
  if (n === 0) return undefined;
  return n === 1 ? '1 upcoming' : `${n} upcoming`;
};

const relativeMinutesLabel = (minutes: number): string => {
  if (minutes <= 0) return 'now';
  if (minutes < 60) return `in ${minutes}m`;
  const hours = Math.round(minutes / 60);
  return `in ${hours}h`;
};

const formatRoutineTime = (routine: Routine): string => {
  const schedule = parseRoutineSchedule(routine.recurrenceRule);
  if (!schedule.triggerTime) return '';
  return formatTimeFromHHmm(schedule.triggerTime);
};

const formatEventDuration = (startDate: Date, endDateMs: number): string => {
  const minutes = Math.max(
    0,
    Math.round((endDateMs - startDate.getTime()) / 60_000)
  );
  if (minutes < 60) return `${minutes} min`;
  const hours = Math.floor(minutes / 60);
  const remainder = minutes % 60;
  return remainder === 0 ? `${hours} hr` : `${hours} hr ${remainder} min`;
};

export type RoutineRowItem = {
  key: string;
  routineId: number;
  state: RoutineState;
  title: string;
  timeLabel: string;
};

export type RoutinesState =
  | { kind: 'loading' }
  | { kind: 'empty-no-routines' }
  | { kind: 'empty-none-today' }
  | { kind: 'populated'; rows: RoutineRowItem[] };

type RoutinesViewProps = {
  state: RoutinesState;
  onOpenRoutine: (routineId: number) => void;
  onCreateRoutine: () => void;
  onManageRoutines: () => void;
};

export const RoutinesView = ({
  state,
  onOpenRoutine,
  onCreateRoutine,
  onManageRoutines,
}: RoutinesViewProps) => (
  <Widget id="todays-routines">
    <Widget.Header
      title="Routines"
      count={
        state.kind === 'populated' && state.rows.length > 0
          ? `${state.rows.length} today`
          : undefined
      }
    />
    <Widget.Body>
      {state.kind === 'loading' && <Widget.Skeleton rows={2} />}

      {state.kind === 'empty-no-routines' && (
        <Widget.Empty
          icon={Icons.refresh}
          title="Set up your first routine"
          action={onCreateRoutine}
        >
          Schedule a recurring task — daily standup digest, weekly review,
          anything.
        </Widget.Empty>
      )}

      {state.kind === 'empty-none-today' && (
        <Widget.Empty
          icon={Icons.refresh}
          title="Nothing scheduled today"
          action={onManageRoutines}
        >
          Your routines will run on their next scheduled day.
        </Widget.Empty>
      )}

      {state.kind === 'populated' && (
        <YStack className="gap-2">
          {ROUTINE_SECTIONS.map(({ state: sectionState, label }) => {
            const rows = state.rows.filter((row) => row.state === sectionState);
            if (rows.length === 0) return null;

            return (
              <YStack key={sectionState} className="gap-0.5">
                <XStack className="items-center gap-2 px-2 opacity-80">
                  <span className="text-xs font-medium text-foreground-muted">
                    {label}
                  </span>
                  <span className="h-0 flex-1 border-t border-dotted border-border-strong" />
                </XStack>
                {rows.map((row) => (
                  <Widget.Row
                    key={row.key}
                    align="center"
                    onClick={() => onOpenRoutine(row.routineId)}
                  >
                    <RoutineStateDot state={row.state} />
                    <span className="w-16 shrink-0 text-xs text-foreground-muted tabular-nums">
                      {row.timeLabel}
                    </span>
                    <span className="flex-1 min-w-0 text-sm text-foreground truncate no-record">
                      {row.title}
                    </span>
                  </Widget.Row>
                ))}
              </YStack>
            );
          })}
        </YStack>
      )}
    </Widget.Body>
  </Widget>
);

export const TodaysRoutinesWidget = observer(() => {
  const { rows, hasAnyRoutine, isLoading } = useTodaysRoutines();
  const { openTab } = useTabs();

  const state: RoutinesState = isLoading
    ? { kind: 'loading' }
    : rows.length === 0
      ? { kind: hasAnyRoutine ? 'empty-none-today' : 'empty-no-routines' }
      : {
          kind: 'populated',
          rows: rows.map(({ routine, state: rowState }) => ({
            key: String(routine.referenceId),
            routineId: routine.referenceId,
            state: rowState,
            title: routine.title,
            timeLabel: formatRoutineTime(routine),
          })),
        };

  return (
    <RoutinesView
      state={state}
      onOpenRoutine={(routineId) => {
        const row =
          state.kind === 'populated'
            ? state.rows.find((r) => r.routineId === routineId)
            : undefined;
        trackEvent('WIDGETS_ROUTINES_ROUTINE_CLICKED', {
          state: row?.state ?? 'idle',
          routine_id: routineId,
          has_report: row?.state === 'ran',
        });
        openTab({ path: `/routines/${routineId}` }, { reuseExisting: true });
      }}
      onCreateRoutine={() => {
        trackEvent('WIDGETS_ROUTINES_CREATE_CLICKED');
        openTab({ path: '/routines' }, { reuseExisting: true });
      }}
      onManageRoutines={() => {
        trackEvent('WIDGETS_ROUTINES_MANAGE_CLICKED');
        openTab({ path: '/routines' }, { reuseExisting: true });
      }}
    />
  );
});

const ROUTINE_SECTIONS = [
  { state: 'ran', label: 'Ran' },
  { state: 'pending', label: 'Scheduled' },
] as const;

const ROUTINE_STATE_DOT_CLASSES: Record<RoutineState, string> = {
  ran: 'bg-brand-emerald-green',
  pending: 'bg-brand-yellow',
  idle: 'bg-foreground-subtle opacity-40',
};

export const RoutineStateDot = ({ state }: { state: RoutineState }) => (
  <span
    className={cn(
      'size-2 rounded-full shrink-0',
      ROUTINE_STATE_DOT_CLASSES[state]
    )}
    aria-hidden
  />
);

export type YesterdayMeetingRow = {
  key: string;
  meetingId: string;
  name: string;
  timeLabel: string;
};

export type YesterdayState =
  | { kind: 'loading' }
  | { kind: 'empty-no-calendar' }
  | { kind: 'empty-no-meetings' }
  | { kind: 'populated'; rows: YesterdayMeetingRow[] };

type YesterdayViewProps = {
  state: YesterdayState;
  onOpenMeeting: (meetingId: string) => void;
  onExtractActionItems: (meetingId: string) => void;
  onConnectCalendar: () => void;
};

export const YesterdayView = ({
  state,
  onOpenMeeting,
  onExtractActionItems,
  onConnectCalendar,
}: YesterdayViewProps) => (
  <Widget id="yesterdays-meetings">
    <Widget.Header title="Yesterday" />
    <Widget.Body>
      {state.kind === 'loading' && <Widget.Skeleton rows={2} />}

      {state.kind === 'empty-no-calendar' && (
        <ConnectCalendarEmpty
          description="Past meetings and action items will land here."
          onConnect={onConnectCalendar}
        />
      )}

      {state.kind === 'empty-no-meetings' && (
        <Widget.Empty icon={Icons.clock} title="No meetings yesterday">
          Yesterday's meeting notes will land here when you have them.
        </Widget.Empty>
      )}

      {state.kind === 'populated' && (
        <YStack className="gap-1">
          {state.rows.map((row) => (
            <Widget.Row
              key={row.key}
              onClick={() => onOpenMeeting(row.meetingId)}
            >
              <YStack className="flex-1 min-w-0 gap-0.5">
                <span className="text-sm text-foreground truncate font-medium no-record">
                  {row.name}
                </span>
                {row.timeLabel && (
                  <span className="text-xs text-foreground-muted">
                    {row.timeLabel}
                  </span>
                )}
              </YStack>
              <Button
                size="dense"
                variant="outline"
                className="shrink-0"
                onClick={(e) => {
                  e.stopPropagation();
                  onExtractActionItems(row.meetingId);
                }}
              >
                Action items
              </Button>
            </Widget.Row>
          ))}
        </YStack>
      )}
    </Widget.Body>
  </Widget>
);

const YESTERDAY_MAX_ROWS = 5;

export const YesterdaysMeetingsWidget = observer(() => {
  const { meetings: yesterdaysMeetings, isLoaded } = useMeetingsOn(
    subDays(new Date(), 1)
  );
  const meetings = yesterdaysMeetings.slice(0, YESTERDAY_MAX_ROWS);
  const { openTab } = useTabs();
  const { setHash } = useHashRoute();
  const hasCalendar = useHasCalendarConnected();

  const state: YesterdayState = !isLoaded
    ? { kind: 'loading' }
    : meetings.length === 0
      ? { kind: hasCalendar ? 'empty-no-meetings' : 'empty-no-calendar' }
      : {
          kind: 'populated',
          rows: meetings.map((meeting) => {
            const ts = meeting.lastTranscribedAt ?? meeting.createdAt;
            return {
              key: meeting.id,
              meetingId: meeting.id,
              name: meeting.name || 'Untitled meeting',
              timeLabel: ts ? format(new Date(ts), 'p') : '',
            };
          }),
        };

  return (
    <YesterdayView
      state={state}
      onOpenMeeting={(meetingId) => {
        trackEvent('WIDGETS_YESTERDAY_MEETING_CLICKED', {
          meeting_id: meetingId,
        });
        openTab({ path: `/meetings/${meetingId}` }, { reuseExisting: true });
      }}
      onExtractActionItems={(meetingId) => {
        trackEvent('WIDGETS_YESTERDAY_EXTRACT_ACTIONS_CLICKED', {
          meeting_id: meetingId,
        });
        openTab({ path: `/meetings/${meetingId}` }, { reuseExisting: true });
        contextChatStore.askQuestion(
          { type: 'meeting', meetingId },
          'What are the action items?'
        );
      }}
      onConnectCalendar={() => {
        trackEvent('WIDGETS_YESTERDAY_CONNECT_CALENDAR_CLICKED', {
          widget: 'yesterday',
        });
        setHash('settings/Integrations');
      }}
    />
  );
});

export type PickUpState =
  | { kind: 'loading' }
  | { kind: 'empty' }
  | {
      kind: 'populated';
      threadId: string;
      title: string;
      timestamp: Date;
    };

type PickUpViewProps = {
  state: PickUpState;
  onResume: (threadId: string) => void;
};

export const PickUpView = ({ state, onResume }: PickUpViewProps) => (
  <Widget id="pick-up-where-you-left-off">
    <Widget.Header title="Continue" />
    <Widget.Body>
      {state.kind === 'loading' && <Widget.Skeleton rows={1} />}

      {state.kind === 'empty' && (
        <Widget.Empty icon={Icons.chat} title="No chats yet">
          Ask Littlebird below — your last thread will live here next time.
        </Widget.Empty>
      )}

      {state.kind === 'populated' && (
        <Widget.Row onClick={() => onResume(state.threadId)}>
          <YStack className="flex-1 min-w-0 gap-0.5">
            <span className="text-sm text-foreground truncate font-medium no-record">
              {state.title}
            </span>
            <span className="text-xs text-foreground-muted">
              {formatDistanceToNow(state.timestamp, { addSuffix: true })}
            </span>
          </YStack>
          <Button
            size="dense"
            variant="outline"
            className="shrink-0"
            onClick={(e) => {
              e.stopPropagation();
              onResume(state.threadId);
            }}
          >
            Resume
          </Button>
        </Widget.Row>
      )}
    </Widget.Body>
  </Widget>
);

export const PickUpWhereYouLeftOffWidget = observer(() => {
  const {
    chats: [thread],
    isLoaded,
  } = useChatsOn();
  const { openTab } = useTabs();

  const state: PickUpState = !isLoaded
    ? { kind: 'loading' }
    : !thread
      ? { kind: 'empty' }
      : {
          kind: 'populated',
          threadId: thread.id,
          title: thread.title || 'Untitled chat',
          timestamp: new Date(
            thread.updatedAt ?? thread.createdAt ?? Date.now()
          ),
        };

  return (
    <PickUpView
      state={state}
      onResume={(threadId) => {
        trackEvent('WIDGETS_PICKUP_RESUMED', {
          resumed_type: 'chat',
          thread_id: threadId,
        });
        openTab({ path: `/chats/${threadId}` }, { reuseExisting: true });
      }}
    />
  );
});

type ConnectMoreApp = {
  key: string;
  label: string;
  icon:
    | { kind: 'component'; Component: ComponentType<{ className?: string }> }
    | { kind: 'image'; src: string };
};

type ConnectMoreState =
  | { kind: 'loading' }
  | { kind: 'good' }
  | { kind: 'populated'; apps: ConnectMoreApp[]; hasMore: boolean };

type ConnectMoreViewProps = {
  state: ConnectMoreState;
  onConnectApp: (key: string) => void;
  onOpenIntegrations: () => void;
};

const CONNECT_MORE_VISIBLE = 3;

const ConnectMoreAppIcon = ({ icon }: { icon: ConnectMoreApp['icon'] }) =>
  icon.kind === 'image' ? (
    <img
      src={icon.src}
      alt=""
      className="size-4 rounded object-contain shrink-0"
    />
  ) : (
    <icon.Component className="size-4 shrink-0" />
  );

export const ConnectMoreView = ({
  state,
  onConnectApp,
  onOpenIntegrations,
}: ConnectMoreViewProps) => (
  <Widget id="connect-more-apps">
    <Widget.Header title="Connect more" />
    <Widget.Body>
      {state.kind === 'loading' && <Widget.Skeleton rows={3} />}

      {state.kind === 'good' && (
        <Widget.Empty
          icon={Icons.check}
          title="Featured apps connected"
          action={onOpenIntegrations}
        >
          Browse the catalog to add more sources.
        </Widget.Empty>
      )}

      {state.kind === 'populated' && (
        <YStack className="gap-0.5">
          <AnimatePresence initial={false} mode="wait">
            <motion.div
              key={state.apps.map((app) => app.key).join('|')}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2, ease: 'easeOut' }}
              className="flex flex-col gap-0.5"
            >
              {state.apps.map((app) => (
                <Widget.Row
                  key={app.key}
                  onClick={() => onConnectApp(app.key)}
                  align="center"
                >
                  <span className="inline-flex items-center justify-center size-7 rounded-md bg-surface-active shrink-0">
                    <ConnectMoreAppIcon icon={app.icon} />
                  </span>
                  <span className="flex-1 min-w-0 truncate text-sm text-foreground">
                    {app.label}
                  </span>
                </Widget.Row>
              ))}
            </motion.div>
          </AnimatePresence>
          {state.hasMore && (
            <button
              type="button"
              onClick={onOpenIntegrations}
              className="px-2 py-1.5 text-left text-xs font-medium text-foreground-muted hover:text-foreground transition-colors"
            >
              More integrations →
            </button>
          )}
        </YStack>
      )}
    </Widget.Body>
  </Widget>
);

const CONNECT_MORE_ROTATE_MS = 30_000;

const useRotatingApps = (apps: ConnectMoreApp[]): ConnectMoreApp[] => {
  const signature = apps.map((a) => a.key).join('|');

  // Shuffle keys, not the objects: the rotation order stays stable across query
  // refetches (keyed on the set, not the array reference), while each rendered
  // row resolves to the current app object so metadata never goes stale.
  const orderedKeys = useMemo(() => {
    const keys = apps.map((a) => a.key);
    for (let i = keys.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [keys[i], keys[j]] = [keys[j], keys[i]];
    }
    return keys;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [signature]);

  const pageCount = Math.ceil(orderedKeys.length / CONNECT_MORE_VISIBLE);
  const [page, setPage] = useState(0);

  useEffect(() => setPage(0), [signature]);

  useEffect(() => {
    if (pageCount <= 1) return;
    const id = setInterval(
      () => setPage((p) => (p + 1) % pageCount),
      CONNECT_MORE_ROTATE_MS
    );
    return () => clearInterval(id);
  }, [pageCount]);

  const byKey = new Map(apps.map((a) => [a.key, a]));
  if (orderedKeys.length <= CONNECT_MORE_VISIBLE) return apps;

  // Clamp the final page to a full window so the widget always shows the same
  // number of rows (the last page overlaps the previous one when the pool size
  // isn't a multiple of CONNECT_MORE_VISIBLE).
  const start = Math.min(
    page * CONNECT_MORE_VISIBLE,
    orderedKeys.length - CONNECT_MORE_VISIBLE
  );
  return orderedKeys
    .slice(start, start + CONNECT_MORE_VISIBLE)
    .map((key) => byKey.get(key))
    .filter((app): app is ConnectMoreApp => app !== undefined);
};

export const ConnectMoreAppsWidget = observer(() => {
  const mcpEnabled = hasFeature(FEATURES.MCP_INTEGRATIONS);
  const { integrationStates, availableIntegrations, connectedAccounts } =
    useIntegrationState();

  const inactiveIntegrations = useMemo(
    () =>
      integrationStates.filter(
        (state) => !state.isConnected && state.info.featured
      ),
    [integrationStates]
  );

  const { data: catalogEntries } = useMCPCatalogQuery(mcpEnabled);
  const { data: mcpServers } = useMCPServersQuery(mcpEnabled);

  const connectedMCPUrls = useMemo(() => {
    if (!mcpServers) return new Set<string>();
    return new Set(
      mcpServers
        .filter(hasConnectedMcpStatus)
        .map((s) => normalizeMCPUrl(s.url))
    );
  }, [mcpServers]);

  const unconnectedCatalog = useMemo(() => {
    if (!mcpEnabled || !catalogEntries) return [];
    return catalogEntries.filter(
      (entry) =>
        entry.featured && !connectedMCPUrls.has(normalizeMCPUrl(entry.url))
    );
  }, [mcpEnabled, catalogEntries, connectedMCPUrls]);

  const apps = useMemo<ConnectMoreApp[]>(
    () => [
      ...inactiveIntegrations.map((s): ConnectMoreApp => ({
        key: s.info.toolkit_name,
        label: s.info.display_name,
        icon: {
          kind: 'component',
          Component: getIntegrationIcon(s.info.toolkit_name),
        },
      })),
      ...unconnectedCatalog.map((entry): ConnectMoreApp => ({
        key: entry.id,
        label: entry.name,
        icon: entry.icon_url
          ? { kind: 'image', src: entry.icon_url }
          : {
              kind: 'component',
              Component: getIntegrationIcon(entry.id),
            },
      })),
    ],
    [inactiveIntegrations, unconnectedCatalog]
  );

  const rotatedApps = useRotatingApps(apps);

  const targetsByKey = new Map<string, IntegrationTarget>();
  for (const s of inactiveIntegrations) {
    targetsByKey.set(s.info.toolkit_name, {
      kind: 'integration',
      toolkitName: s.info.toolkit_name,
      displayName: s.info.display_name,
      requiresPaidPlan: s.info.requires_paid_plan,
    });
  }
  for (const entry of unconnectedCatalog) {
    targetsByKey.set(entry.id, {
      kind: 'mcp',
      catalogId: entry.id,
      displayName: entry.name,
      url: entry.url,
      requiresPaidPlan: entry.requires_paid_plan,
    });
  }

  const isLoading =
    availableIntegrations === undefined ||
    connectedAccounts === undefined ||
    (mcpEnabled && (catalogEntries === undefined || mcpServers === undefined));

  const state: ConnectMoreState = isLoading
    ? { kind: 'loading' }
    : apps.length === 0
      ? { kind: 'good' }
      : {
          kind: 'populated',
          apps: rotatedApps,
          hasMore: apps.length > CONNECT_MORE_VISIBLE,
        };

  return (
    <ConnectMoreView
      state={state}
      onConnectApp={(key) => {
        const target = targetsByKey.get(key);
        if (!target) return;
        trackEvent('WIDGETS_CONNECT_MORE_CLICKED', { from: 'gaps' });
        trackEvent('WIDGETS_CONNECT_INTEGRATION_CLICKED', {
          integration: key,
          kind: target.kind,
          display_name: target.displayName,
          shown_count: apps.length,
        });
        void connectIntegration(target, `widget_connect_more:${key}`);
      }}
      onOpenIntegrations={() => {
        trackEvent('WIDGETS_CONNECT_MORE_CLICKED', {
          from: state.kind === 'good' ? 'good' : 'gaps',
        });
        ModalsManager.show(AddIntegrationsModal, {});
      }}
    />
  );
});

export type CreditBalanceState =
  | { kind: 'loading' }
  | { kind: 'empty'; formatted: string }
  | { kind: 'populated'; formatted: string; planLabel: string };

type CreditBalanceViewProps = {
  state: CreditBalanceState;
  onTopUp: () => void;
  onManage: () => void;
};

export const CreditBalanceView = ({
  state,
  onTopUp,
  onManage,
}: CreditBalanceViewProps) => (
  <Widget id="credit-balance">
    <Widget.Header title="Credits" />
    <Widget.Body>
      {state.kind === 'loading' && <Widget.Skeleton rows={1} />}

      {state.kind === 'empty' && (
        <XStack className="items-end justify-between gap-3 px-2 py-1.5">
          <YStack className="gap-0.5">
            <span className="text-xl font-medium tracking-tight leading-none text-foreground-muted">
              {state.formatted}
            </span>
            <span className="text-xs text-foreground-muted">
              Top up to keep chatting
            </span>
          </YStack>
          <Button size="dense" variant="default" onClick={onTopUp}>
            Top up
          </Button>
        </XStack>
      )}

      {state.kind === 'populated' && (
        <XStack className="items-end justify-between gap-3 px-2 py-1.5">
          <YStack className="gap-0.5">
            <span className="text-xl font-medium tracking-tight leading-none text-foreground">
              {state.formatted}
            </span>
            <span className="text-xs text-foreground-muted">
              {state.planLabel}
            </span>
          </YStack>
          <Button size="dense" variant="outline" onClick={onManage}>
            Manage
          </Button>
        </XStack>
      )}
    </Widget.Body>
  </Widget>
);

export const CreditBalanceWidget = observer(() => {
  const cents = subscriptionStore.state.accountCreditCents;
  const { setHash } = useHashRoute();

  const state: CreditBalanceState =
    cents === undefined
      ? { kind: 'loading' }
      : cents === 0
        ? { kind: 'empty', formatted: formatDollars(0) }
        : {
            kind: 'populated',
            formatted: formatDollars(cents),
            planLabel: planLabel(subscriptionStore.currentPlan),
          };

  return (
    <CreditBalanceView
      state={state}
      onTopUp={() => {
        trackEvent('WIDGETS_CREDIT_TOPUP_CLICKED', {
          from: state.kind === 'populated' ? 'populated' : 'empty',
        });
        setHash('settings/Subscription');
      }}
      onManage={() => {
        trackEvent('WIDGETS_CREDIT_MANAGE_CLICKED');
        setHash('settings/Subscription');
      }}
    />
  );
});

const formatDollars = (cents: number): string =>
  new Intl.NumberFormat(undefined, {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
  }).format(cents / 100);

const planLabel = (plan: string): string =>
  plan
    .toLowerCase()
    .split('_')
    .map((s) => s.charAt(0).toUpperCase() + s.slice(1))
    .join(' ');
